import { fetchVanSessionSummary, saveVanMemory } from "../network/van"
import { getSession } from "../auth/storage"

// Relay summary job polling, mirroring the native VanSessionSummaryPoller.
// Pending sessions live in localStorage so a reload or closed tab can resume them.
const PENDING_KEY = "vantero.menu.vanPendingSummaries"
const POLL_INTERVAL_MS = 1_000
const MAX_WAIT_MS = 5 * 60_000
const MAX_CONSECUTIVE_MISSES = 30
const PENDING_TTL_MS = 24 * 60 * 60_000

const inFlight = new Set()

// Summary result -> PUT /van/memory/ update; null when there is nothing to write.
// An empty paragraph falls back to the session's old_memory, since "" clears the stored one.
export function readSummaryResult(result, fallbackMemory) {
  if (!result || typeof result !== "object") return null

  const resultMemory = typeof result.memory === "string" ? result.memory : ""
  const memory = resultMemory || fallbackMemory || ""
  const update = { memory }
  if (result.score !== undefined && result.score !== null) update.score = result.score
  if (typeof result.score_explanation === "string") update.scoreExplanation = result.score_explanation

  return memory || update.score !== undefined ? update : null
}

export function pollAndSaveSummary({ sessionId, restaurantId, itemId, fallbackMemory, userId }) {
  if (!sessionId || !restaurantId || !itemId || !userId) return
  const entry = {
    sessionId,
    restaurantId,
    itemId,
    fallbackMemory: fallbackMemory || null,
    userId: userId ?? null,
    createdAt: Date.now()
  }
  writePending([...readPending().filter((pending) => pending.sessionId !== sessionId), entry])
  poll(entry)
}

// Other users' entries are kept for them — the iPad is shared between admins.
export function resumePendingSummaries(userId) {
  if (!userId) return
  const now = Date.now()
  const live = readPending().filter(
    (pending) => pending.userId && now - pending.createdAt <= PENDING_TTL_MS
  )
  writePending(live)
  live.filter((pending) => pending.userId === userId).forEach(poll)
}

async function poll(entry) {
  if (inFlight.has(entry.sessionId)) return
  inFlight.add(entry.sessionId)
  try {
    const outcome = await awaitOutcome(entry)
    if (outcome.type === "succeeded") {
      await save(outcome.result, entry)
    } else if (outcome.type === "failed" || outcome.type === "notFound") {
      removePending(entry.sessionId)
    }
    // unfinished: kept for the next resume
  } catch {
    // A failed save keeps the entry; the result is still retrievable from the relay.
  } finally {
    inFlight.delete(entry.sessionId)
  }
}

async function save(result, entry) {
  // The PUT is authorised as the signed-in user in the current restaurant
  const current = getSession()
  if (current?.user?.id !== entry.userId || current?.restaurant?.id !== entry.restaurantId) return

  // A newer session on the same item already saved and dropped this entry
  if (!readPending().some((pending) => pending.sessionId === entry.sessionId)) return

  const update = readSummaryResult(result, entry.fallbackMemory)
  if (update) await saveVanMemory(entry.itemId, update)

  // Older sessions on this item would overwrite the newer memory if resumed later
  writePending(
    readPending().filter(
      (pending) =>
        pending.sessionId !== entry.sessionId &&
        !(pending.userId === entry.userId && pending.itemId === entry.itemId && pending.createdAt < entry.createdAt)
    )
  )
}

// 404s end the poll as notFound; outages and the time cap leave it unfinished.
async function awaitOutcome(entry) {
  const deadline = Date.now() + MAX_WAIT_MS
  let notFoundStreak = 0
  let unavailableStreak = 0

  while (Date.now() < deadline) {
    let job
    try {
      job = await fetchVanSessionSummary(entry.sessionId, entry.restaurantId)
    } catch {
      job = undefined
    }

    if (job === null) {
      unavailableStreak = 0
      notFoundStreak += 1
      if (notFoundStreak >= MAX_CONSECUTIVE_MISSES) return { type: "notFound" }
    } else if (job === undefined) {
      unavailableStreak += 1
      if (unavailableStreak >= MAX_CONSECUTIVE_MISSES) return { type: "unfinished" }
    } else {
      notFoundStreak = 0
      unavailableStreak = 0
      if (job.status === "succeeded") return { type: "succeeded", result: job.result }
      if (job.status === "failed") return { type: "failed", error: job.error }
    }
    await sleep(POLL_INTERVAL_MS)
  }
  return { type: "unfinished" }
}

function readPending() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_KEY) ?? "[]")
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writePending(entries) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(entries))
  } catch {
    // Storage full or blocked: polling still runs for this page's lifetime.
  }
}

function removePending(sessionId) {
  writePending(readPending().filter((pending) => pending.sessionId !== sessionId))
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
